/**
 * AR Technical Demo (F3 / GEN-2259) — mock Crestline vendor portal.
 *
 * THROWAWAY DEMO CODE. A frontend-only screen styled as a generic vendor
 * portal with Crestline branding. It is purely REPRESENTATIVE: the real
 * operator / web-browser infra exists but is NOT called. Instead it plays a
 * REAL screen recording of Nami driving Crestline's actual vendor portal
 * (`crestlinevendors.lovable.app`), framed in browser chrome so it reads like
 * a live agent session.
 *
 * It renders as a POPUP OVERLAY on top of the current page (the deduction
 * collection thread) rather than a separate route — `<CrestlinePortalOverlay>`
 * is mounted once in the authenticated layout and shows whenever
 * `portal-store.open` is true. Two flows (storyboard Flow 2), one clip each,
 * picked by `portal-store.mode`:
 *  - Scenes 7-9 (`backups`): sign in → find #CR-DED-99214 → download the
 *    backup PDFs. When the clip ends, the tool fires F0's
 *    `applyPortalBackupsFiled`, so the backups land as evidence on deduction
 *    thread COL-1043 (the handoff is a pure fixture-state flip).
 *  - Scene 11 (`claim`): open the dispute form → fill ($3,100, supporting
 *    docs) → submit. When the clip ends, the tool fires F0's `applyClaimFiled`.
 *
 * The recordings are hosted off-repo (S3) and referenced by URL — swap the
 * placeholders in `PORTAL_VIDEOS` for the uploaded links. The overlay reports
 * the clip finishing via `markPortalVideoEnded()` so the scripted tool can
 * continue (and then `closePortal()`s the overlay). Delete with the rest of
 * `src/demo/ar`.
 */

import { createPortal } from 'react-dom';
import { T, Var, useGT } from 'gt-react';
import { StorefrontIcon, RobotIcon } from '@phosphor-icons/react';
import {
  markPortalVideoEnded,
  usePortalStore,
  type PortalMode,
  type PortalUiState,
} from './portal-store';
import { TECH_BRAND } from './fixture-seed-technical';

// Non-translatable demo literals bound out of JSX (i18nsure jsx-only mode).
const PORTAL_URL = 'https://vendors.crestlinemarkets.com/deductions';

// Real screen recordings of Nami operating Crestline's vendor portal, hosted
// off-repo. SWAP THESE for the uploaded S3 URLs (one clip per flow).
const PORTAL_VIDEOS: Record<PortalMode, string> = {
  backups:
    'https://pillar-demo-public-assets-778057248606-us-east-2-an.s3.us-east-2.amazonaws.com/crestline-portal.webm',
  claim:
    'https://pillar-demo-public-assets-778057248606-us-east-2-an.s3.us-east-2.amazonaws.com/crestline-claim.webm',
};

const selectState = (s: PortalUiState): PortalUiState => s;

// ---------------------------------------------------------------------------
// Chrome — vendor-portal frame with Crestline branding + live-agent indicator.
// ---------------------------------------------------------------------------
function PortalChrome({
  agentNote,
  children,
}: {
  agentNote: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="overflow-hidden rounded-xl border bg-background shadow-2xl">
        {/* Browser-ish address bar */}
        <div className="flex items-center gap-2 border-b bg-muted/60 px-4 py-2">
          <span className="flex gap-1.5">
            <span className="size-2.5 rounded-full bg-red-400" />
            <span className="size-2.5 rounded-full bg-yellow-400" />
            <span className="size-2.5 rounded-full bg-green-400" />
          </span>
          <div className="ml-2 flex-1 truncate rounded bg-background px-3 py-1 text-xs text-muted-foreground">
            {PORTAL_URL}
          </div>
          <span className="flex shrink-0 items-center gap-1.5 rounded-md bg-red-500/15 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-red-600">
            <span className="size-1.5 animate-pulse rounded-full bg-red-500" />
            <T context="portal live agent session badge">Agent session</T>
          </span>
        </div>

        {/* Portal header */}
        <div className="flex items-center justify-between bg-emerald-800 px-5 py-3 text-white">
          <span className="flex items-center gap-2 font-semibold">
            <StorefrontIcon weight="fill" className="size-5" />
            <T context="mock vendor portal brand">
              <Var>{TECH_BRAND.customerName}</Var> Vendor Portal
            </T>
          </span>
        </div>

        {/* Agent-operating indicator */}
        <div className="flex items-center gap-2 border-b bg-purple-50 px-5 py-2 text-sm text-purple-800">
          <RobotIcon weight="fill" className="size-4 shrink-0" />
          <span aria-live="polite">{agentNote}</span>
        </div>

        <div className="bg-black">{children}</div>
      </div>
      <p className="mt-3 text-center text-xs text-white/80">
        <T context="portal live agent feed caption">
          Agent live feed — Gently is operating Crestline’s vendor portal on
          your behalf.
        </T>
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Agent narration note per flow + phase.
// ---------------------------------------------------------------------------
function useAgentNote(state: PortalUiState): string {
  const gt = useGT();
  const done = state.phase === 'done';

  if (state.mode === 'backups') {
    return done
      ? gt(
          'Agent: backups downloaded and filed to deduction thread COL-1043.',
          {
            context: 'portal agent note backups done',
          },
        )
      : gt(
          "Agent: signed in to Crestline's portal — pulling #CR-DED-99214 backup documents…",
          { context: 'portal agent note backups running' },
        );
  }

  return done
    ? gt('Agent: dispute filed — confirmation received from Crestline.', {
        context: 'portal agent note claim done',
      })
    : gt(
        "Agent: filing the $3,100 dispute for #CR-DED-99214 on Crestline's portal…",
        { context: 'portal agent note claim running' },
      );
}

/** The portal session — browser chrome + the recorded agent video. */
function PortalSession({ state }: { state: PortalUiState }) {
  const agentNote = useAgentNote(state);
  return (
    <PortalChrome agentNote={agentNote}>
      <video
        // Remount (and replay from the top) on every portal run.
        key={`${state.mode}-${state.runId}`}
        src={PORTAL_VIDEOS[state.mode]}
        autoPlay
        muted
        playsInline
        controls
        className="aspect-video w-full"
        onEnded={markPortalVideoEnded}
        onError={markPortalVideoEnded}
      >
        <T context="portal video fallback">
          Your browser does not support the video tag.
        </T>
      </video>
    </PortalChrome>
  );
}

/**
 * The portal popup. Mounted once in the authenticated layout (next to
 * `ArDemoControls`); renders a fixed modal over the current page — i.e. over
 * the deduction collection thread — whenever a portal flow is open. The
 * scripted tools (`registerPortalTools.tsx`) drive open → wait → close.
 */
export function CrestlinePortalOverlay() {
  const state = usePortalStore(selectState);
  if (!state.open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
    >
      <PortalSession state={state} />
    </div>,
    document.body,
  );
}
